<script setup>
// One part, opened from its card: what it is, what it holds, who sees and
// manages it, the rules that matter, what needs to exist before it and what
// comes next, what it holds and is held by inside its area, and its links to
// other areas as chips. A TlDialog on desktop; the same
// dialog docks to the bottom as a sheet on a phone (content-class smd-sheet,
// styled in SystemMap.vue).
import TlDialog from '~/components/TlDialog.vue';
import SystemMapArt from '~/components/systemMap/SystemMapArt.vue';
import SystemMapChip from '~/components/systemMap/SystemMapChip.vue';
import {findSystemMapNode, findSystemMapSection, systemMapSteps} from '~/utils/systemMap';

const props = defineProps({
  modelValue: {type: Boolean, default: false},
  node: {type: Object, default: null},
  flags: {type: Object, default: () => ({})},
  // Every connection (systemMapConnections): the holds ones are listed here
  connections: {type: Array, default: () => []},
  // Links to other areas (crossAreaLinks): shown as chips
  crossLinks: {type: Array, default: () => []},
  // The parts on show (visibleSystemMapIds): steps to hidden admin parts are left out
  visible: {type: Object, default: null},
});
const emit = defineEmits(['update:modelValue', 'jump', 'section']);

const open = computed({
  get: () => props.modelValue,
  set: (v) => emit('update:modelValue', v),
});
const section = computed(() => findSystemMapSection(props.node?.section));
const held = computed(() => props.connections.filter((c) => c.kind === 'holds'));
const sameAreaLinks = computed(() => {
  const cross = new Set(props.crossLinks.map((c) => c.id));
  return props.connections.filter((c) => c.kind === 'link' && !cross.has(c.id));
});
const nameOf = (id) => findSystemMapNode(id)?.name || id;
const areaOf = (id) => findSystemMapSection(findSystemMapNode(id)?.section)?.name || '';
const connectionLine = (c) => {
  const other = nameOf(c.id);
  if (c.kind === 'holds') return c.label === 'held by' ? `held by ${other}` : `holds ${other}`;
  if (c.say) return c.say;
  return c.reverse ? `${other} ${c.label} this` : `${c.label} ${other}`;
};
const visible = (id) => !props.visible || props.visible.has(id);
const steps = computed(() => (props.node ? systemMapSteps(props.node.id) : {before: [], after: []}));
const needsFirst = computed(() => steps.value.before.filter((c) => visible(c.id)));
const comesNext = computed(() => steps.value.after.filter((c) => visible(c.id)));
// A step to another area is listed under Needs first / Comes next above, so it is not a chip here as well (R51 L5)
const connectedChips = computed(() => props.crossLinks.filter((c) => c.kind !== 'step'));
</script>

<template>
  <!-- close-on-back is off: the map owns Back (opening pushed an entry, so Back closes the dialog and only a second Back leaves the area) -->
  <tl-dialog v-model="open" :max-width="560" content-class="smd-sheet" :close-on-back="false">
    <template #title>
      <span v-if="node" class="smd-title">
        <system-map-art :name="node.art" :size="30"/>
        <span>{{ node.name }}</span>
      </span>
    </template>
    <template v-if="node">
      <div class="smd-tags">
        <button v-if="section" type="button" class="tl-chip smd-section" @click="emit('section', section.id)">
          <v-icon size="13">mdi-map-marker-outline</v-icon>{{ section.name }}
        </button>
        <span v-if="flags.admin" class="tl-chip"><v-icon size="13">mdi-lock-outline</v-icon>Admin feature</span>
      </div>

      <p class="smd-what">{{ node.what }}</p>

      <div v-if="node.holds?.length" class="smd-block">
        <h3>What it holds</h3>
        <ul class="smd-list">
          <li v-for="line in node.holds" :key="line"><v-icon size="15" icon="mdi-circle-small"/><span>{{ line }}</span></li>
        </ul>
      </div>

      <div class="smd-who">
        <div class="smd-who-cell">
          <span class="smd-eyebrow">Who sees it</span>
          <span>{{ node.sees }}</span>
        </div>
        <div class="smd-who-cell">
          <span class="smd-eyebrow">Who manages it</span>
          <span>{{ node.manages }}</span>
        </div>
      </div>

      <div v-if="node.rules?.length" class="smd-block">
        <h3>Rules that matter</h3>
        <ul class="smd-list smd-list--rules">
          <li v-for="line in node.rules" :key="line"><v-icon size="15" icon="mdi-check-circle" class="smd-check"/><span>{{ line }}</span></li>
        </ul>
      </div>

      <div v-if="needsFirst.length || comesNext.length" class="smd-block smd-block--steps">
        <div v-if="needsFirst.length" class="smd-steps">
          <h3>Needs first</h3>
          <div class="smd-links">
            <button v-for="c in needsFirst" :key="`b-${c.id}`" type="button" class="smd-link smd-link--step" @click="emit('jump', c.id)">
              <system-map-art :name="findSystemMapNode(c.id)?.art || 'grid'" :size="22"/>
              <span class="smd-link-text">
                <span class="smd-link-name">{{ nameOf(c.id) }}</span>
                <span class="smd-link-rel">before this · {{ areaOf(c.id) }}</span>
              </span>
              <v-icon size="16">mdi-arrow-right</v-icon>
            </button>
          </div>
        </div>
        <div v-if="comesNext.length" class="smd-steps">
          <h3>Comes next</h3>
          <div class="smd-links">
            <button v-for="c in comesNext" :key="`a-${c.id}`" type="button" class="smd-link smd-link--step" @click="emit('jump', c.id)">
              <system-map-art :name="findSystemMapNode(c.id)?.art || 'grid'" :size="22"/>
              <span class="smd-link-text">
                <span class="smd-link-name">{{ nameOf(c.id) }}</span>
                <span class="smd-link-rel">after this · {{ areaOf(c.id) }}</span>
              </span>
              <v-icon size="16">mdi-arrow-right</v-icon>
            </button>
          </div>
        </div>
      </div>

      <div v-if="held.length || sameAreaLinks.length" class="smd-block">
        <h3>In {{ section?.name || 'this area' }}</h3>
        <div class="smd-links">
          <button
              v-for="c in [...held, ...sameAreaLinks]"
              :key="`${c.kind}-${c.id}`"
              type="button"
              class="smd-link"
              :class="{'smd-link--holds': c.kind === 'holds'}"
              @click="emit('jump', c.id)"
          >
            <system-map-art :name="findSystemMapNode(c.id)?.art || 'grid'" :size="22"/>
            <span class="smd-link-text">
              <span class="smd-link-name">{{ nameOf(c.id) }}</span>
              <span class="smd-link-rel">{{ connectionLine(c) }}</span>
            </span>
            <v-icon size="16">mdi-arrow-right</v-icon>
          </button>
        </div>
      </div>

      <div v-if="connectedChips.length" class="smd-block">
        <h3>Connected to</h3>
        <div class="smd-chips">
          <system-map-chip v-for="c in connectedChips" :key="`x-${c.id}`" :link="c" size="md" :from-name="node.name" @jump="(id) => emit('jump', id)"/>
        </div>
      </div>

      <div v-if="node.related?.length" class="smd-block">
        <h3>See also</h3>
        <div class="smd-related">
          <button v-for="id in node.related" :key="id" type="button" class="tl-chip smd-related-chip" @click="emit('jump', id)">
            {{ nameOf(id) }}
          </button>
        </div>
      </div>
    </template>
  </tl-dialog>
</template>

<style scoped lang="scss">
.smd-title {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.smd-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 12px;
}

.smd-section {
  cursor: pointer;
  font-family: inherit;
}

.smd-what {
  font-size: 14.5px;
  line-height: 1.6;
  color: rgb(var(--v-theme-text));
  margin: 0 0 14px;
}

.smd-block {
  margin-top: 16px;

  h3 {
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    color: rgb(var(--v-theme-textSubtle));
    margin: 0 0 8px;
  }
}

.smd-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 6px;

  li {
    display: flex;
    gap: 6px;
    align-items: flex-start;
    font-size: 13.5px;
    line-height: 1.5;
    color: rgb(var(--v-theme-text));

    .v-icon {
      flex: none;
      margin-top: 2px;
      color: rgb(var(--v-theme-charcoal));
    }
  }
}

.smd-check {
  color: rgb(var(--v-theme-success)) !important;
}

.smd-who {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-top: 16px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
}

.smd-who-cell {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 10px 12px;
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 10px;
  background: rgb(var(--v-theme-light));
  font-size: 13px;
  line-height: 1.45;
}

.smd-eyebrow {
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: rgb(var(--v-theme-textSubtle));
}

.smd-links {
  display: grid;
  gap: 6px;
}

.smd-block--steps {
  display: grid;
  gap: 12px;
}

.smd-link {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 10px;
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 10px;
  background: rgb(var(--v-theme-surface));
  text-align: left;
  cursor: pointer;
  font-family: inherit;
  color: rgb(var(--v-theme-text));

  &:hover {
    border-color: rgb(var(--v-theme-border));
    background: rgb(var(--v-theme-light));
  }

  &:focus-visible {
    outline: 2px solid rgb(var(--v-theme-primary));
    outline-offset: 1px;
  }

  &--holds {
    border-left: 3px solid rgb(var(--v-theme-primary));
  }

  &--step {
    border-left: 3px solid rgb(var(--v-theme-info));
  }

  .v-icon:last-child {
    margin-left: auto;
    color: rgb(var(--v-theme-charcoal));
  }
}

.smd-link-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.smd-link-name {
  font-size: 13.5px;
  font-weight: 700;
}

.smd-link-rel {
  font-size: 11.5px;
  color: rgb(var(--v-theme-charcoal));
}

.smd-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.smd-related {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.smd-related-chip {
  cursor: pointer;
  font-family: inherit;

  &:hover {
    border-color: rgb(var(--v-theme-border));
  }
}
</style>
